import {
  ContentDeliveryService,
  ContentstackConfig,
  ContentstackConfigService,
} from '@csp/csp-common-content-delivery-client';
import { MemCacheService } from '@csp/csp-common-memcache';
import { ApiOptions, ClientType, CspError, StateAssert } from '@csp/csp-common-model';
import {
  ConsentDocumentContentModel,
  GenericPackageResourceContent,
  GenericPodContentPackage,
  NotificationContentModel,
  StudyPodPatientContentPackage,
} from '@csp/csp-pod-patient-content-definitions';
import {
  getPodGenericResourceContentBlock,
  getPodGenericResourceContentEntry,
} from '../util/getPodGenericContentResourceBlock';
import { rawToQuestionnaireContent } from '../util/rawToQuestionnaireContent';
import { QuestionnaireContent } from '../model/QuestionnaireContent';

const GENERIC_NOTIFICATION_CACHE_KEY = 'generic-notifications';
const STUDY_QUESTIONNAIRE_CACHE_KEY = 'study-questionnaire';
const STUDY_SYSTEM_TEXTS_CACHE_KEY = 'study-systemTexts';
const STUDY_NOTIFICATION_CACHE_KEY = 'study-notifications';
export const GENERIC_CONSENT_CACHE_KEY_PREFIX = 'generic-consent';

export const ttl = 30 * 60; //30 minutes

export type PatientClientType = Extract<ClientType, ClientType.PATIENT_APP | ClientType.PATIENT_WEB>;

const getConfig = (locale: string, clientType: PatientClientType = ClientType.PATIENT_APP): ContentstackConfig => ({
  ...ContentstackConfigService.get(),
  locale,
  clientType,
  role: 'patient',
});

const getConsentDocuments = async (locale: string, apiOptions?: ApiOptions): Promise<ConsentDocumentContentModel[]> => {
  const genericContentPackage = await fetchGenericPackage(locale, apiOptions);
  return genericContentPackage.consent_document;
};

const getGenericNotificationContent = async (
  locale: string,
  apiOptions?: ApiOptions,
): Promise<NotificationContentModel[]> => {
  const cached = MemCacheService.getValue<NotificationContentModel[]>([GENERIC_NOTIFICATION_CACHE_KEY, locale]);
  if (cached) {
    return cached;
  }
  const genericContentPackage = await fetchGenericPackage(locale, apiOptions);
  MemCacheService.setValue([GENERIC_NOTIFICATION_CACHE_KEY, locale], ttl, genericContentPackage.notification);
  return genericContentPackage.notification;
};

const getStudyNotificationContent = async (
  locale: string,
  dCode: string,
  apiOptions?: ApiOptions,
): Promise<NotificationContentModel[]> => {
  const cached = MemCacheService.getValue<NotificationContentModel[]>([STUDY_NOTIFICATION_CACHE_KEY, dCode, locale]);
  if (cached) {
    return cached;
  }
  const studyContentPackage = await fetchStudyPackage(locale, apiOptions);
  MemCacheService.setValue([STUDY_NOTIFICATION_CACHE_KEY, dCode, locale], ttl, studyContentPackage.notification);
  return studyContentPackage.notification;
};

const getNotifications = async (locale: string, apiOptions?: ApiOptions): Promise<NotificationContentModel[]> => {
  const genericContentPackage = await fetchGenericPackage(locale, apiOptions);
  return genericContentPackage.notification;
};

const getQuestionnaireByCode = async (
  locale: string,
  questionnaireCode: string,
  apiOptions?: ApiOptions,
): Promise<QuestionnaireContent> => {
  const cachedQuestionnaire = MemCacheService.getValue<QuestionnaireContent>([
    STUDY_QUESTIONNAIRE_CACHE_KEY,
    questionnaireCode,
    locale,
  ]);

  if (cachedQuestionnaire) {
    return cachedQuestionnaire;
  }

  const genericContentPackage = await fetchStudyPackage(locale, apiOptions);
  const questionnaireRaw = genericContentPackage.questionnaire.find(
    questionnaire => questionnaire.questionnaire_code === questionnaireCode,
  );

  if (!questionnaireRaw) {
    throw CspError.notFound(`Questionnaire with code: ${questionnaireCode} does not exist in ContentStack`);
  }

  const questionnaire = rawToQuestionnaireContent(questionnaireRaw);

  MemCacheService.setValue<QuestionnaireContent>(
    [STUDY_QUESTIONNAIRE_CACHE_KEY, questionnaireRaw.questionnaire_code, locale],
    ttl,
    questionnaire,
  );

  return questionnaire;
};

const getAllQuestionnairesByCodes = async (
  locale: string,
  questionnaireCodes: string[],
  apiOptions?: ApiOptions,
): Promise<QuestionnaireContent[]> => {
  const initialAccumulator = {
    cachedQuestionnaires: [] as QuestionnaireContent[],
    questionnaireCodesToFetch: [] as string[],
  };

  const { cachedQuestionnaires, questionnaireCodesToFetch } = questionnaireCodes.reduce((acc, questionnaireCode) => {
    const cachedQuestionnaire = MemCacheService.getValue<QuestionnaireContent>([
      STUDY_QUESTIONNAIRE_CACHE_KEY,
      questionnaireCode,
      locale,
    ]);
    cachedQuestionnaire
      ? acc.cachedQuestionnaires.push(cachedQuestionnaire)
      : acc.questionnaireCodesToFetch.push(questionnaireCode);

    return acc;
  }, initialAccumulator);

  if (!questionnaireCodesToFetch.length) {
    return cachedQuestionnaires;
  }

  const studyContentPackage = await fetchStudyPackage(locale, apiOptions);

  StateAssert.isDefined(studyContentPackage.questionnaire, 'Questionnaire missing from study content package.');

  return studyContentPackage.questionnaire.reduce((accumulator, questionnaireRaw) => {
    if (!questionnaireCodes.includes(questionnaireRaw.questionnaire_code)) {
      return accumulator;
    }

    const questionnaire = rawToQuestionnaireContent(questionnaireRaw);

    MemCacheService.setValue<QuestionnaireContent>(
      [STUDY_NOTIFICATION_CACHE_KEY, questionnaire.questionnaire_code, locale],
      ttl,
      questionnaire,
    );

    accumulator.push(questionnaire);

    return accumulator;
  }, [] as QuestionnaireContent[]);
};

const getResource = async (locale: string, apiOptions?: ApiOptions): Promise<GenericPackageResourceContent> => {
  const cachedSystemTexts = MemCacheService.getValue<GenericPackageResourceContent>([
    STUDY_SYSTEM_TEXTS_CACHE_KEY,
    locale,
  ]);

  if (cachedSystemTexts) {
    return cachedSystemTexts;
  }

  const genericPackage = await fetchGenericPackage(locale, apiOptions);

  MemCacheService.setValue<GenericPackageResourceContent>(
    [STUDY_SYSTEM_TEXTS_CACHE_KEY, locale],
    ttl,
    genericPackage.resource,
  );

  return genericPackage.resource;
};

const fetchGenericPackage = async (
  locale: string,
  apiOptions?: ApiOptions,
  clientType?: PatientClientType,
): Promise<GenericPodContentPackage> => {
  const contentPackage = await ContentDeliveryService.fetchContentPackage(
    'generic',
    apiOptions,
    getConfig(locale, clientType),
  );
  return contentPackage as unknown as GenericPodContentPackage;
};

const fetchStudyPackage = async (locale: string, apiOptions?: ApiOptions): Promise<StudyPodPatientContentPackage> => {
  const contentPackage = await ContentDeliveryService.fetchContentPackage('study', apiOptions, getConfig(locale));
  return contentPackage as unknown as StudyPodPatientContentPackage;
};

const getGenericEntry = async <EntryKey extends keyof GenericPackageResourceContent>(
  entryIdentifier: EntryKey,
  locale: string,
  apiOptions?: ApiOptions,
): Promise<GenericPackageResourceContent[EntryKey]> => {
  const content = await getResource(locale, apiOptions);

  const entry = getPodGenericResourceContentEntry(content, entryIdentifier);

  if (!entry) {
    throw CspError.badState(`Entry ${entryIdentifier} does not exist`);
  }

  return entry;
};

const getGenericBlock = async <
  EntryKey extends keyof GenericPackageResourceContent,
  BlockKey extends keyof GenericPackageResourceContent[EntryKey],
>(
  entryIdentifier: EntryKey,
  blockIdentifier: BlockKey,
  locale: string,
  apiOptions?: ApiOptions,
): Promise<GenericPackageResourceContent[EntryKey][BlockKey]> => {
  const content = await getResource(locale, apiOptions);
  const block = getPodGenericResourceContentBlock(content, entryIdentifier, blockIdentifier);

  if (!block) {
    throw CspError.badState(`Block ${String(blockIdentifier)} in entry ${entryIdentifier} does not exist`);
  }

  return block;
};

/**
 * Content service for fetching content for patients in csp-pod. All functions in this service expects a locale and an axios instance that has a valid patient token.
 */
export const PatientContentService = {
  fetchGenericPackage,
  // @deprecated
  getConsentDocuments,
  getGenericNotificationContent,
  getStudyNotificationContent,
  getNotifications,
  getQuestionnaireByCode,
  getAllQuestionnairesByCodes,
  getResource,
  getGenericEntry,
  getGenericBlock,
  fetchStudyPackage,
};
